// Formatting for people: times, sizes, percentages.

const MINUTE = 60;
const HOUR = 3600;
const DAY = 86400;

export function parseTime(value: string | null | undefined): number | null {
  if (!value) return null;
  const time = Date.parse(value);
  return Number.isNaN(time) ? null : time;
}

/** "just now", "5 min ago", "3 h ago", "2 days ago". */
export function timeAgo(value: string | null | undefined, now: number = Date.now()): string {
  const time = parseTime(value);
  if (time === null) return "never";
  const seconds = Math.round((now - time) / 1000);
  if (seconds < -30) return "in the future";
  if (seconds < 10) return "just now";
  if (seconds < MINUTE) return `${seconds} s ago`;
  if (seconds < HOUR) return `${Math.floor(seconds / MINUTE)} min ago`;
  if (seconds < DAY) return `${Math.floor(seconds / HOUR)} h ago`;
  const days = Math.floor(seconds / DAY);
  return days === 1 ? "yesterday" : `${days} days ago`;
}

/** "2 h 5 min", for how long something took or lasted. */
export function duration(milliseconds: number): string {
  const seconds = Math.max(0, Math.round(milliseconds / 1000));
  if (seconds < MINUTE) return `${seconds} s`;
  if (seconds < HOUR) {
    const rest = seconds % MINUTE;
    return rest ? `${Math.floor(seconds / MINUTE)} min ${rest} s` : `${seconds / MINUTE} min`;
  }
  if (seconds < DAY) {
    const minutes = Math.floor((seconds % HOUR) / MINUTE);
    return minutes ? `${Math.floor(seconds / HOUR)} h ${minutes} min` : `${seconds / HOUR} h`;
  }
  const hours = Math.floor((seconds % DAY) / HOUR);
  const days = Math.floor(seconds / DAY);
  return hours ? `${days} d ${hours} h` : `${days} d`;
}

const dateTime = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});
const timeOnly = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" });
const dayAndTime = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

export function formatDateTime(value: string | null | undefined): string {
  const time = parseTime(value);
  return time === null ? "—" : dateTime.format(time);
}

/** For chart axes: the time alone within a day, the day too beyond that. */
export function formatTick(time: number, spanMs: number): string {
  return spanMs <= DAY * 1000 ? timeOnly.format(time) : dayAndTime.format(time);
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 100 ? value.toFixed(0) : value.toFixed(1)} ${units[unit]}`;
}

export function formatMegabytes(megabytes: number | null | undefined): string {
  return megabytes === null || megabytes === undefined ? "—" : formatBytes(megabytes * 1024 * 1024);
}

const compact = new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 });
const plain = new Intl.NumberFormat();

/** 1,284 stays as it is; 12,900 becomes 12.9K. */
export function formatCount(value: number): string {
  return Math.abs(value) < 10_000 ? plain.format(value) : compact.format(value);
}

export function formatNumber(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined) return "—";
  return value.toFixed(digits);
}

export function percentOf(part: number | null | undefined, whole: number | null | undefined) {
  if (part === null || part === undefined || !whole) return null;
  return Math.min(100, Math.max(0, (100 * part) / whole));
}

/** "reboot" -> "Reboot", "low_battery" -> "Low battery". */
export function humanize(identifier: string): string {
  const words = identifier.replace(/[_.]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
